import css from '@styled-system/css'
import * as React from 'react'
import styled, { StyledComponent, ThemeContext } from 'styled-components'
import {
  color,
  ColorProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
} from 'styled-system'

const SvgIcon = (
  props: React.SVGProps<SVGSVGElement> & {
    size?: number | string
    color?: string
  },
) => {
  const { size } = props

  if (size != null) {
    props = {
      width: size,
      height: size,
      ...props,
    }
  }

  const themeContext = React.useContext(ThemeContext)

  if (props.color != null) {
    props = { ...props, color: themeContext.colors[props.color] || props.color }
  }

  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 86 86"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clipPath="url(#setting_svg__clip0)">
        <path
          d="M79.32 45.293c.028-.716.057-1.404.057-2.121 0-.745-.029-1.462-.086-2.179l6.65-2.293c-.114-1.175-.257-2.38-.487-3.555-.143-.802-.315-1.605-.487-2.408l-7.11.058c-.4-1.376-.917-2.724-1.461-4.014l5.504-4.443c-.516-1.06-1.061-2.15-1.663-3.182a31.61 31.61 0 00-1.29-2.093l-6.708 2.494a37.311 37.311 0 00-2.752-3.239l3.67-6.106a53.172 53.172 0 00-2.667-2.408c-.63-.516-1.261-1.032-1.92-1.52l-5.476 4.673a35.865 35.865 0 00-3.669-2.121l1.347-6.995a67.52 67.52 0 00-3.325-1.376 32.203 32.203 0 00-2.35-.774l-3.555 6.25a39.878 39.878 0 00-4.186-.746L46.21.143A50.841 50.841 0 0042.569 0c-.831 0-1.663.029-2.465.057l-1.176 7.11c-1.404.143-2.809.401-4.185.716l-3.469-6.249a52.95 52.95 0 00-3.44 1.09c-.774.286-1.548.573-2.293.888l1.319 7.052a39.392 39.392 0 00-3.698 2.093L17.744 8.14a50.946 50.946 0 00-2.867 2.208c-.63.516-1.233 1.06-1.835 1.634l3.67 6.163c-.975 1.032-1.921 2.093-2.781 3.24l-6.65-2.523A36.326 36.326 0 005.36 21.9a79.737 79.737 0 00-1.176 2.15l5.504 4.53a37.346 37.346 0 00-1.519 4.013l-7.052-.115c-.315 1.147-.545 2.322-.774 3.498a61.984 61.984 0 00-.373 2.436l6.68 2.351a33.995 33.995 0 00-.086 2.437c0 .63.028 1.261.057 1.892l-6.622 2.293c.115 1.175.287 2.38.487 3.555.144.802.316 1.605.488 2.408l7.023-.058a33.11 33.11 0 001.462 4.1l-5.39 4.357c.517 1.06 1.061 2.15 1.663 3.182.402.717.832 1.405 1.29 2.093l6.536-2.437a35.151 35.151 0 002.81 3.383l-3.555 5.905c.86.831 1.749 1.634 2.666 2.408.63.516 1.29 1.032 1.92 1.52l5.275-4.473c1.233.832 2.494 1.548 3.842 2.236l-1.29 6.708c1.089.488 2.207.946 3.325 1.376.774.287 1.548.545 2.35.774l3.412-5.991c1.433.344 2.895.602 4.386.774l1.09 6.737a50.04 50.04 0 003.611.143c.832 0 1.663-.029 2.466-.057l1.146-6.794c1.491-.172 2.953-.43 4.386-.746l3.326 5.963a52.93 52.93 0 003.44-1.09c.774-.286 1.548-.573 2.293-.917l-1.261-6.765a39.188 39.188 0 003.841-2.207l5.189 4.472a40.274 40.274 0 002.866-2.208c.631-.516 1.233-1.06 1.835-1.634l-3.555-5.962c1.004-1.061 1.978-2.179 2.838-3.354l6.479 2.465a36.327 36.327 0 001.92-3.039c.402-.716.803-1.433 1.176-2.15l-5.418-4.443a33.24 33.24 0 001.49-4.1l6.967.115c.315-1.146.573-2.322.774-3.497.143-.803.258-1.605.372-2.437l-6.65-2.408zM42.999 70.864c-15.394 0-27.864-12.413-27.864-27.72 0-15.309 12.47-27.721 27.864-27.721s27.864 12.412 27.864 27.72c0 15.308-12.47 27.721-27.864 27.721z"
          fill={props.color || '#000'}
        />
      </g>
      <defs>
        <clipPath id="setting_svg__clip0">
          <path fill="#fff" d="M0 0h86v86H0z" />
        </clipPath>
      </defs>
    </svg>
  )
}

type StyledSystemProps = TypographyProps &
  SpaceProps &
  LayoutProps &
  ColorProps &
  PositionProps

const _style = (
  component: React.FC,
): StyledComponent<
  React.FC<
    React.SVGProps<SVGSVGElement> & {
      size?: number | string
      color?: string
    }
  >,
  Record<string, unknown>,
  StyledSystemProps,
  never
> => {
  const styledSystemProps = [typography, space, layout, color, position]
  return styled(component)(css({}), styledSystemProps)
}

const SvgSetting = _style(SvgIcon)

export default SvgSetting
